// LegalPageLayout.jsx
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { sanitize } from 'isomorphic-dompurify';
import { color, headerHeight } from '../style/variables.js';
import { ResetHeadingStyle } from '../style/ResetHeadingStyle.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

import BackIcon from '../../img/ic_topbar_back.svg';

const SANITIZE_CONFIG = { ADD_TAGS: ['style'], FORCE_BODY: true };

export const LegalPageLayout = ({
  title,
  htmlString,
  goBack = NULL_FUNCTION,
  onMailTo = NULL_FUNCTION,
}) => {
  const dangerousHtml = useMemo(
    () => ({ __html: sanitize(htmlString, SANITIZE_CONFIG) }),
    [htmlString]
  );

  const handleClick = useCallback(
    e => {
      const anchor = e.target.closest('a[href^="mailto:"]');
      if (anchor) {
        e.preventDefault();
        onMailTo();
      }
    },
    [onMailTo]
  );

  return (
    <StyledLegalPageLayout>
      <Header>
        <CloseButton role="button" onClick={goBack}>
          <CloseIcon src={BackIcon} title="close" />
        </CloseButton>
        <HeaderTitle>{title}</HeaderTitle>
      </Header>
      <Body>
        <Description
          dangerouslySetInnerHTML={dangerousHtml}
          onClick={handleClick}
        />
      </Body>
    </StyledLegalPageLayout>
  );
};

LegalPageLayout.propTypes = {
  title: PropTypes.string.isRequired,
  htmlString: PropTypes.string.isRequired,
  goBack: PropTypes.func,
  onMailTo: PropTypes.func,
};

export default LegalPageLayout;

const StyledLegalPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  width: 100%;
  height: 100%;
  background-color: ${color.black};
  padding-top: var(--space-to-pinned-notification);
`;

const Header = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: ${headerHeight}px;
  background-color: ${color.neutral[10]};
`;

const CloseButton = styled.div`
  box-sizing: content-box;
  position: absolute;
  top: 0;
  left: 0;
  padding: 14px 16px;
  cursor: pointer;
  height: 30px;
  :hover {
    opacity: 0.7;
  }
`;

const CloseIcon = styled.img`
  height: 100%;
  width: auto;
  vertical-align: top;
`;

const HeaderTitle = styled.h1`
  flex: none;
  ${ResetHeadingStyle}
  font-size: 16px;
  color: ${color.white};
`;

const Body = styled.div`
  border-bottom: 1px solid ${color.neutral['30_alpha_50']};
  border-top: 1px solid ${color.neutral['30_alpha_50']};
  padding: 20px 16px;
  background-color: ${color.neutral[10]};
  overflow: auto;
`;

const Description = styled.div`
  font-size: 14px;
  text-align: justify;
  line-height: 1.43;
`;
